import { useState } from 'react';
import { SetupTaskSlot } from './briefingComposition';
import { SETUP_HIDE_DAYS, SETUP_LOCAL_ONLY_NOTE } from './setupTaskState';
import CardIndexColumn from './CardIndexColumn';

/**
 * 세팅·정착 과제 카드 — **회원 카드와 같은 골격** (2026-09-01 QA29, 대표 지시).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 격상했나
 * ─────────────────────────────────────────────────────────────
 * 신규 센터에는 회원 카드가 **0건**이다. 그 화면에서 세팅 과제는 「곁다리」가
 * 아니라 **그날 할 일의 전부**다. 그런데 예전 카드는 번호도 체크박스도 없는
 * 얇은 띠라, 원장님이 아침에 열면 「오늘 챙길 일」은 비어 있고 그 위에 안내문만
 * 얹힌 화면이었다. 진행률도 0/0 이라 채울 수가 없었다.
 *
 * 이제 회원 카드와 같은 UI 를 쓴다 — 좌측 번호(회원 카드와 이어지는 시퀀스),
 * 체크박스, 가리기, 배지, 제목, 대상 수치·명단, 행동 안내.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 그래도 **「지시하기」 버튼은 없다**
 * ─────────────────────────────────────────────────────────────
 * 체크·가리기는 이 브라우저에 남기면 되지만, 지시는 **보낼 대상이 없다.**
 * 세팅 과제는 서버 브리핑 항목이 아니라 `memberId`·`coachId` 로 묶인 지시를
 * 만들 수 없고, 버튼만 같게 두면 원장님이 눌러 놓고 「보냈다」고 믿는다.
 * (2026-08-28 지시의 「체크박스 금지」는 2026-09-01 지시로 대체됐지만,
 *  「지시하기 금지」는 그대로 살아 있다)
 *
 * ⚠️ 체크·가리기는 **이 브라우저에만** 남는다(`setupTaskState`).
 *    카드가 그 한계를 직접 밝힌다 — 다른 기기에서 다시 뜨는 것을 보고
 *    「저장이 안 된다」로 읽으면 안 된다.
 *
 * ⚠️ 화면에 「신규 센터」·「과도기」라고 쓰지 않습니다 (결정 ④).
 *    분류는 내부 판정 전용이고, 원장님에게는 **할 일만** 적습니다.
 */

const BORDER = '#C9D9EE';
const RAIL = '#8FB0DE';
const TINT = '#F2F7FF';

/**
 * 이름 칩을 몇 개까지 세우나 (2026-09-01 QA34).
 *
 * 서버가 표본을 최대 20건 주지만 전부 세우면 카드가 다시 길어진다.
 * 여섯이면 한 줄 반, 「누구인지」를 감 잡기에 충분하고 나머지는 수로 밝힌다.
 */
const NAME_CHIPS = 6;

const LABEL: Record<SetupTaskSlot['task'], string> = {
    ONBOARDING: '센터 설정',
    COACH_SETTLING: '강사 정착',
};

type Props = {
    slot: SetupTaskSlot;
    /**
     * 카드 왼쪽 번호 — **회원 카드와 이어지는 시퀀스**다.
     *
     * 회원 카드가 1~3 이면 과제는 4 부터다. 따로 1 부터 매기면 화면에 1 이
     * 두 번 뜨고, 원장님이 「오늘 챙길 일 5건」을 세다가 틀린다.
     */
    index: number;
    checked: boolean;
    onToggleCheck: (id: string) => void;
    onHide: (id: string) => void;
};

const SetupTaskCard = ({ slot, index, checked, onToggleCheck, onHide }: Props) => {
    const [hoverCheck, setHoverCheck] = useState(false);

    return (
        <div
            className="mb-[12px] flex w-full overflow-hidden rounded-[16px] border border-solid"
            style={{
                borderColor: BORDER,
                background: '#FFFFFF',
                opacity: checked ? 0.6 : 1,
            }}
            aria-label={`${LABEL[slot.task]} 과제 · ${slot.title}`}
        >
            {/*
              순번 + 체크박스 — 회원 카드와 **같은 컴포넌트**다 (QA35).
              예전에는 값을 손으로 맞춰 뒀는데, 레일이 빠져 있었고 카드가
              짧아지자 번호가 아래 모서리에 붙었다 (`CardIndexColumn` 머리말).
            */}
            <CardIndexColumn rail={RAIL} tint={TINT} accent="#2C4C8C" index={index}>
                <button
                    type="button"
                    onClick={() => {
                        setHoverCheck(false);
                        onToggleCheck(slot.id);
                    }}
                    onMouseEnter={() => setHoverCheck(true)}
                    onMouseLeave={() => setHoverCheck(false)}
                    title={`완료로 표시 · ${SETUP_LOCAL_ONLY_NOTE}`}
                    aria-label={`${slot.title} 완료로 표시`}
                    className="flex items-center justify-center"
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        background: checked || hoverCheck ? '#00A793' : '#FFFFFF',
                        border:
                            checked || hoverCheck ? '1px solid #00A793' : `2px solid ${RAIL}`,
                        color: '#FFFFFF',
                        fontSize: 19,
                        fontWeight: 800,
                        cursor: 'pointer',
                        transition: 'background .15s, border-color .15s',
                        padding: 0,
                    }}
                >
                    {checked || hoverCheck ? '✓' : ''}
                </button>
            </CardIndexColumn>

            <div className="flex min-w-0 flex-1 flex-col" style={{ padding: '13px 20px 14px 8px' }}>
                <div className="flex items-center gap-[10px]" style={{ flexWrap: 'wrap' }}>
                    {/* 성격 배지 — 회원 카드의 등급 배지 자리다 */}
                    <span
                        className="rounded-[6px]"
                        title={`센터 설정 과제입니다 · 설정을 마치면 사라집니다\n체크·가리기는 ${SETUP_LOCAL_ONLY_NOTE}`}
                        style={{
                            background: '#DCE7F8',
                            color: '#2C4C8C',
                            fontSize: 12.5,
                            fontWeight: 800,
                            padding: '4px 10px',
                            letterSpacing: '0.02em',
                            flex: 'none',
                            cursor: 'help',
                        }}
                    >
                        {LABEL[slot.task]}
                    </span>
                    <span
                        style={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: '#16202E',
                            textDecoration: checked ? 'line-through' : 'none',
                        }}
                    >
                        {slot.title}
                    </span>

                    {/*
                      🔴 대상 수치 — 회원 카드의 키 지표 자리.
                         `null` 은 **못 잰 것**이라 숫자를 그리지 않는다.
                         0 으로 적으면 「끝났다」가 되는데 사실이 아니다.
                    */}
                    {slot.count != null && (
                        /*
                          🔴 **숫자가 주인공이다** (2026-09-01 QA34, 대표 지시).
                             설명 문장을 걷어낸 자리를 이 배지가 대신한다 —
                             회원 카드의 금액 배지와 **같은 위계**로 올린다.

                          ⚠️ 단위(`강사`/`회원`)를 빼지 마라. 「회원 배정 · 3명」은
                             회원 셋으로 읽히지만 실제로는 담당 회원이 없는
                             **강사** 3명이다 (`setupTasks.ONBOARDING_UNIT`).
                             문장을 지우면서 이 사실까지 지우면 숫자가 거짓말을 한다.

                          색은 이 카드가 이미 쓰는 배지 톤 그대로다. 새 색을 만들지 않는다.
                        */
                        <span
                            className="inline-flex items-baseline rounded-[8px]"
                            style={{
                                background: '#DCE7F8',
                                padding: '5px 11px',
                                gap: 3,
                                flex: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {slot.unit && (
                                <span
                                    style={{ fontSize: 12, fontWeight: 700, color: '#5B7CB8' }}
                                >
                                    {slot.unit}
                                </span>
                            )}
                            <span
                                style={{
                                    fontSize: 21,
                                    fontWeight: 800,
                                    color: '#2C4C8C',
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                {slot.count.toLocaleString('ko-KR')}
                            </span>
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#5B7CB8' }}>
                                명 남음
                            </span>
                        </span>
                    )}

                    {/*
                      가리기 — 회원 카드와 **같은 의미론**이다(사흘 억제).
                      「했다」가 아니라 「오늘은 안 볼래」라서 체크와 자리를 나눈다.
                    */}
                    <button
                        type="button"
                        onClick={() => onHide(slot.id)}
                        title={`${SETUP_HIDE_DAYS}일간 이 과제를 다시 알리지 않습니다 · ${SETUP_LOCAL_ONLY_NOTE}`}
                        aria-label={`${slot.title} 가리기`}
                        className="ml-auto rounded-[7px]"
                        style={{
                            height: 30,
                            padding: '0 11px',
                            background: '#FFFFFF',
                            border: `1px solid ${BORDER}`,
                            color: '#7C8794',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            flex: 'none',
                        }}
                    >
                        가리기
                    </button>
                </div>

                {/*
                  한 줄 부연 — **제목이 다 말하지 못하는 과제에만** 붙는다.
                  세팅 축에는 안 붙는다(제목·숫자의 되풀이라 대표 지시로 뺐다).
                  강사 정착만 「무엇을 하면 풀리는가」를 여기서 말한다.
                */}
                {slot.note && (
                    <p style={{ fontSize: 13.5, color: '#5B6B7F', marginTop: 7, lineHeight: '20px' }}>
                        {slot.note}
                    </p>
                )}

                {/*
                  🔴 **대상 명단은 칩으로** (2026-09-01 QA34, 대표 지시).

                  예전에는 「대상」 라벨이 붙은 상자 안에 이름을 가운뎃점으로
                  이어 붙인 줄글이었다. 상자 하나가 카드 높이의 삼분의 일을
                  먹었고, 과제가 일곱 장이면 한 화면에 안 들어갔다.

                  칩은 이름 하나가 한 덩어리로 읽히고 줄바꿈도 이름 단위로
                  일어난다. 색은 이 카드가 이미 쓰는 톤 그대로다 —
                  새 색을 만들지 않는다.

                  ⚠️ 서버가 최대 20건만 주므로 화면도 그 이상을 약속하지 않는다.
                     여섯 명까지 세우고 나머지는 「외 N명」으로 접는다.
                */}
                {slot.names.length > 0 && (
                    <div
                        className="flex flex-wrap items-center"
                        style={{ gap: 5, marginTop: 8 }}
                    >
                        {slot.names.slice(0, NAME_CHIPS).map((n) => (
                            <span
                                key={n}
                                className="rounded-[6px]"
                                style={{
                                    background: TINT,
                                    border: `1px solid ${BORDER}`,
                                    color: '#5B6B7F',
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    padding: '3px 9px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {n}
                            </span>
                        ))}
                        {slot.names.length > NAME_CHIPS && (
                            <span
                                style={{
                                    fontSize: 12.5,
                                    color: '#7C8794',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                외 {slot.names.length - NAME_CHIPS}명
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SetupTaskCard;
